"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Menu, X, Wind } from "lucide-react"
import { cn } from "@/lib/utils"

const navLinks = [
  { label: "Shop", href: "/shop" },
  { label: "Subscribe", href: "/subscribe" },
  { label: "Locations", href: "/locations" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
]

type NavProps = {
  overDarkHero?: boolean
}

export default function Nav({ overDarkHero = false }: NavProps) {
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener("scroll", onScroll)
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-[#FDFAF3]/95 backdrop-blur-md shadow-sm border-b border-[#C4622A]/10"
          : "bg-transparent"
      )}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
            <Wind className="w-5 h-5 text-primary-foreground" />
          </div>
          <div className="flex flex-col leading-none">
            <span
              className={cn(
                "font-heading font-bold text-[15px] tracking-wide",
                overDarkHero && !scrolled ? "text-primary-foreground" : "text-foreground"
              )}
            >
              Northwind
            </span>
            <span
              className={cn(
                "text-[10px] tracking-[0.15em] uppercase",
                overDarkHero && !scrolled ? "text-primary-foreground/70" : "text-muted-foreground"
              )}
            >
              Coffee Roasters
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={cn(
                "text-sm font-medium transition-colors",
                overDarkHero && !scrolled
                  ? "text-primary-foreground hover:text-primary"
                  : "text-foreground/70 hover:text-primary"
              )}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <Link
            href="/wholesale"
            className={cn(
              "text-sm transition-colors",
              overDarkHero && !scrolled
                ? "text-primary-foreground hover:text-primary"
                : "text-muted-foreground hover:text-primary"
            )}
          >
            Wholesale
          </Link>
          <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-5 shadow-md">
            Shop Beans
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          className={cn(
            "lg:hidden p-1 transition-colors",
            overDarkHero && !scrolled ? "text-primary-foreground" : "text-foreground"
          )}
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#FDFAF3] border-t border-border px-6 py-6 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={() => setOpen(false)}
              className="text-base font-medium text-foreground hover:text-primary transition-colors"
            >
              {link.label}
            </Link>
          ))}
          <Link href="/wholesale" onClick={() => setOpen(false)} className="text-base text-muted-foreground">
            Wholesale
          </Link>
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground w-full mt-2 rounded-full">Shop Beans</Button>
        </div>
      )}
    </header>
  )
}
